import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

const COL_DEFS = [
  { key: 'srcEl',  label: 'Origen',       minWidth: 160 },
  { key: 'dstEl',  label: 'Destino',      minWidth: 160 },
  { key: 'cable',  label: 'Tipo cable',   minWidth: 110 },
  { key: 'color',  label: 'Color',        minWidth: 90  },
  { key: 'length', label: 'Longitud',     minWidth: 80  },
  { key: 'notes',  label: 'Notas',        minWidth: 160 },
];

const CABLE_HEX = {
  negro: 'var(--text-primary)', gris: 'var(--text-muted)', blanco: 'var(--border)', rojo: 'var(--red-600)',
  azul: 'var(--accent)', verde: 'var(--green-600)', amarillo: 'var(--amber-600)', naranja: 'var(--warn-text)',
  violeta: 'var(--accent-2)', celeste: 'var(--accent-l)',
};

const CABLE_TYPES = [
  'Cat5e', 'Cat6', 'Cat6a', 'Cat7', 'Cat8',
  'Fibra MM (OM3/OM4)', 'Fibra SM (OS2)',
  'DAC (cobre directo)', 'AOC (fibra activa)', 'Otro',
];

const CABLE_COLORS = [
  { value: 'negro',    label: 'Negro' },
  { value: 'gris',     label: 'Gris' },
  { value: 'blanco',   label: 'Blanco' },
  { value: 'rojo',     label: 'Rojo' },
  { value: 'azul',     label: 'Azul' },
  { value: 'verde',    label: 'Verde' },
  { value: 'amarillo', label: 'Amarillo' },
  { value: 'naranja',  label: 'Naranja' },
  { value: 'violeta',  label: 'Violeta' },
  { value: 'celeste',  label: 'Celeste' },
];

function flatRow(c) {
  const pa = c.port_a || {};
  const pb = c.port_b || {};
  return {
    id:     c.id,
    srcEl:  pa.element_name  || '—',
    srcPt:  pa.label         || (pa.port_number  ? `P${pa.port_number}`  : '?'),
    dstEl:  pb.element_name  || '—',
    dstPt:  pb.label         || (pb.port_number  ? `P${pb.port_number}`  : '?'),
    cable:  c.cable_type     || '',
    color:  c.cable_color    || '',
    lengthM: c.cable_length_m,
    length: c.cable_length_m != null ? `${c.cable_length_m} m` : '',
    notes:  c.notes          || '',
  };
}

export default function ConnectionsMatrix({ companyId }) {
  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [search,    setSearch]    = useState('');
  const [colFilter, setColFilter] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editData,  setEditData]  = useState({});
  const [saving,    setSaving]    = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(null);
    try {
      const res = await api.getAllConnections(companyId);
      setRows((res.data || []).map(flatRow));
    } catch (e) { setError(e.message); }
    finally     { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(row => {
      if (q) {
        const all = `${row.srcEl} ${row.srcPt} ${row.dstEl} ${row.dstPt} ${row.cable} ${row.color} ${row.length} ${row.notes}`.toLowerCase();
        if (!all.includes(q)) return false;
      }
      return Object.entries(colFilter).every(([k, v]) =>
        !v.trim() || (row[k] || '').toLowerCase().includes(v.toLowerCase())
      );
    });
  }, [rows, search, colFilter]);

  const setCol = (key, val) => setColFilter(prev => ({ ...prev, [key]: val }));

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditData({ cable: row.cable, color: row.color, lengthM: row.lengthM, notes: row.notes });
  };

  const cancelEdit = () => { setEditingId(null); setEditData({}); };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await api.updateConnection(editingId, {
        cable_type: editData.cable || null,
        cable_color: editData.color || null,
        cable_length_m: editData.lengthM != null && editData.lengthM !== '' ? Number(editData.lengthM) : null,
        notes: editData.notes || null,
      }, companyId);
      // Optimistic update
      setRows(prev => prev.map(r => r.id === editingId ? {
        ...r,
        cable: editData.cable || '',
        color: editData.color || '',
        lengthM: editData.lengthM != null && editData.lengthM !== '' ? Number(editData.lengthM) : null,
        length: editData.lengthM != null && editData.lengthM !== '' ? `${editData.lengthM} m` : '',
        notes: editData.notes || '',
      } : r));
      setEditingId(null); setEditData({});
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (!companyId) return (
    <div style={{ padding: '2rem', color: 'var(--text-muted)', textAlign: 'center', fontFamily: 'system-ui' }}>
      Selecciona una empresa para ver la matriz de conexiones.
    </div>
  );

  const Dash = () => <span style={{ color: 'var(--border)' }}>—</span>;
  const cellP = { padding: '0.55rem 0.9rem' };
  const inputS = { padding: '3px 6px', borderRadius: 5, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.78rem', width: '100%', boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={{ padding: '1.5rem 2rem', fontFamily: 'system-ui,sans-serif', background: 'var(--bg-card)', minHeight: 'calc(100vh - 60px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.3rem', color: 'var(--text-primary)' }}>🔌 Matriz de Conexiones</h2>
          <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            {loading
              ? 'Cargando…'
              : `${filtered.length} / ${rows.length} conexión${rows.length !== 1 ? 'es' : ''}`}
          </p>
        </div>
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Buscar en toda la tabla…"
          style={{ marginLeft: 'auto', padding: '0.5rem 0.85rem', borderRadius: 8, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.85rem', width: 280, outline: 'none' }}
        />
        <button onClick={load}
          style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>
          ↺ Actualizar
        </button>
      </div>

      {error && (
        <div style={{ background: 'var(--error-bg)', color: 'var(--error-text)', border: '1px solid var(--error-bd)', padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid var(--slate-300)', background: 'var(--bg-toolbar)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ background: 'var(--bg-input)', borderBottom: '2px solid var(--slate-300)' }}>
              {COL_DEFS.map(c => (
                <th key={c.key} style={{ padding: '0.6rem 0.9rem', textAlign: 'left', fontWeight: 700, color: 'var(--text-secondary)', minWidth: c.minWidth }}>
                  <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 4 }}>{c.label}</div>
                  <input
                    type="text" value={colFilter[c.key] || ''} onChange={e => setCol(c.key, e.target.value)}
                    placeholder="Filtrar…"
                    style={{ padding: '3px 6px', borderRadius: 5, border: '1px solid var(--slate-300)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.73rem', width: '100%', boxSizing: 'border-box', outline: 'none' }}
                  />
                </th>
              ))}
              <th style={{ padding: '0.6rem 0.9rem', textAlign: 'center', fontWeight: 700, color: 'var(--text-secondary)', minWidth: 60 }}>
                <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', fontWeight: 600 }}>Acción</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Cargando conexiones…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Sin resultados</td></tr>
            )}
            {!loading && filtered.map((row, i) => {
              const isEditing = editingId === row.id;
              return (
                <tr key={row.id} style={{ borderBottom: '1px solid var(--slate-300)', background: isEditing ? 'var(--warn-bg)' : i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-toolbar)' }}>
                  <td style={cellP}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{row.srcEl}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.73rem' }}>{row.srcPt}</div>
                  </td>
                  <td style={cellP}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{row.dstEl}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.73rem' }}>{row.dstPt}</div>
                  </td>
                  {/* Cable type */}
                  <td style={cellP}>
                    {isEditing ? (
                      <select value={editData.cable || ''} onChange={e => setEditData(d => ({ ...d, cable: e.target.value }))} style={inputS}>
                        <option value="">—</option>
                        {CABLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    ) : (
                      <span style={{ color: 'var(--text-secondary)' }}>{row.cable || <Dash />}</span>
                    )}
                  </td>
                  {/* Color */}
                  <td style={cellP}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                        {CABLE_COLORS.map(c => (
                          <span key={c.value}
                            onClick={() => setEditData(d => ({ ...d, color: d.color === c.value ? '' : c.value }))}
                            style={{
                              width: 18, height: 18, borderRadius: '50%', cursor: 'pointer',
                              background: CABLE_HEX[c.value] || 'var(--text-muted)', display: 'inline-block',
                              border: editData.color === c.value ? '2px solid var(--text-primary)' : '1px solid var(--slate-300)',
                              boxSizing: 'border-box',
                            }}
                            title={c.label}
                          />
                        ))}
                      </div>
                    ) : row.color ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 14, height: 14, borderRadius: '50%', background: CABLE_HEX[row.color] || 'var(--text-muted)', display: 'inline-block', border: '1px solid var(--slate-300)', flexShrink: 0 }} />
                        <span style={{ color: 'var(--text-secondary)' }}>{row.color}</span>
                      </span>
                    ) : <Dash />}
                  </td>
                  {/* Length */}
                  <td style={cellP}>
                    {isEditing ? (
                      <input type="number" min="0" step="0.1"
                        value={editData.lengthM != null ? editData.lengthM : ''}
                        onChange={e => setEditData(d => ({ ...d, lengthM: e.target.value }))}
                        placeholder="m" style={{ ...inputS, width: 70 }}
                      />
                    ) : (
                      <span style={{ color: 'var(--text-secondary)' }}>{row.length || <Dash />}</span>
                    )}
                  </td>
                  {/* Notes */}
                  <td style={{ ...cellP, maxWidth: 240 }}>
                    {isEditing ? (
                      <input type="text"
                        value={editData.notes || ''}
                        onChange={e => setEditData(d => ({ ...d, notes: e.target.value }))}
                        placeholder="Notas…" style={inputS}
                      />
                    ) : (
                      <span style={{ color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }} title={row.notes}>
                        {row.notes || <Dash />}
                      </span>
                    )}
                  </td>
                  {/* Actions */}
                  <td style={{ ...cellP, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <button onClick={saveEdit} disabled={saving}
                          style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid var(--green-600)', background: 'var(--green-50)', color: 'var(--green-600)', fontSize: '0.73rem', cursor: 'pointer', fontWeight: 600 }}>
                          {saving ? '…' : '✓'}
                        </button>
                        <button onClick={cancelEdit}
                          style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', color: 'var(--text-muted)', fontSize: '0.73rem', cursor: 'pointer' }}>
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(row)}
                        style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid var(--slate-300)', background: 'var(--bg-input)', color: 'var(--text-secondary)', fontSize: '0.73rem', cursor: 'pointer' }}>
                        ✎
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {!loading && filtered.length > 0 && (
            <tfoot>
              <tr style={{ background: 'var(--bg-input)', borderTop: '2px solid var(--slate-300)' }}>
                <td colSpan={7} style={{ padding: '0.45rem 0.9rem', fontSize: '0.73rem', color: 'var(--text-muted)' }}>
                  {filtered.length} conexión{filtered.length !== 1 ? 'es' : ''} mostrada{filtered.length !== 1 ? 's' : ''}
                  {rows.length !== filtered.length && ` (de ${rows.length} total)`}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
